import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Dropdown, Input, message, Select, Spin, Tooltip, TreeSelect } from 'antd'
import { FaEdit, FaSearch } from 'react-icons/fa'
import { PiWarningCircleLight } from 'react-icons/pi'

import './style.scss'
import uris from '../../../config/uris'
import CustomTable from '../../../assets/component/table'
import CustomModal from '../../../assets/component/modal'
import Breadcrumbs from '../../../assets/component/breadcrumbs'
import ManageFilters from '../../../assets/component/manage-filters'
import { getParentAndChildrenOptions } from '../../../helpers/array-format'
import { decryptData } from '../../../helpers/encrypt/crypto-encrypt'
import { filterFolders } from '../../esg-chat/esg-chat'
import { findNodeByValue, getAllDescendantNodes } from '../../../helpers/tree-select'
import { playbookNameDropdown, playbookNameDropdownReset } from '../../../store/slices/manage-playbook/playbookNameDropdown'
import { questionWiseUpdate } from '../../../store/slices/playbook-batch/updateQuestionData'
import { disclosureTypes } from '../../../store/slices/playbook-batch/disclosureTypes'
import { playbookBatchQuestions, playbookBatchQuestionsReset } from '../../../store/slices/playbook-batch/viewPlaybookQuestions'
import { createPlaybookBatch, createPlaybookBatchReset, PrefillPlaybookSourceYearAction, PrefillPlaybookSourceYearReset } from '../../../store/slices/manage-playbook/playbookBatch'
import PrioritizeSource from './prioritize-source'
import { getUserFolderDetails } from '../../../store/slices/playbook-batch/userWiseFolderList'
import { antIcon } from '../../custom-playbook/manage-playbook'

const menuData = {
    title: 'Create PlaybookBatch',
    parentMenu: {
        name: 'ESG Playbooks',
        link: uris?.esgPlaybookRegulations,
    },
    subMenu: {
        name: 'Create Batch',
        link: uris?.createBatch,
    },
}

const filterOptions = [
    {
        value: 'All',
        label: 'All',
    },
    {
        value: 'Include',
        label: 'Include',
    },
]

const ModelOptions = [
    { label: "Advanced Research", value: "sonnet" },
    { label: "Basic Research", value: "haiku" }
];

const updateQuestionRow = (question, key, value, setUpdatedQuestionRow) => {
    setUpdatedQuestionRow((prevRows) => {
        const existingIndex = prevRows.findIndex((q) => q.id === question.id);
        let updatedRows = [...prevRows];

        if (existingIndex !== -1) {
            updatedRows[existingIndex] = { ...updatedRows[existingIndex], [key]: value, isUpdated: true };
        } else {
            updatedRows.push({ ...question, [key]: value, isUpdated: true });
        }

        return updatedRows;
    });
}

const Year = ({ data, yearOptions, setUpdatedQuestionRow, defaultYear, setSelectedRowKeys }) => {
    const [selectedYear, setSelectedYear] = useState(data?.year)
    const [updatedYear, setUpdatedYear] = useState(null)

    const updateQuestionYear = (event, question) => {
        setSelectedYear(event)
        setUpdatedYear(null)
        updateQuestionRow(question, "year", event, setUpdatedQuestionRow)
        setSelectedRowKeys((prevData) => {
            const newRowKeys = new Set(prevData)
            newRowKeys.add(question?.id)
            return Array.from(newRowKeys)
        })
    }

    useEffect(() => {
        setUpdatedYear(defaultYear)
    }, [defaultYear])

    return (
        <div>
            <Select
                className="source-select"
                options={yearOptions}
                placeholder="Select Year"
                value={updatedYear || selectedYear}
                onChange={(e) => updateQuestionYear(e, data)}
                filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
            />
        </div>
    )
}

const FilterType = ({ setFilterType, filterType, data, index, dispatch, updatedQuestionRow, setUpdatedQuestionRow, setSelectedRowKeys, defaultYear }) => {

    const updateQuestionFilterType = (event, question) => {
        setFilterType((prev) => ({
            ...prev,
            [question.id]: event,
        }));
        updateQuestionRow(question, "filterType", event, setUpdatedQuestionRow)
        setSelectedRowKeys((prevData) => {
            const newRowKeys = new Set(prevData)
            newRowKeys.add(question?.id)
            return Array.from(newRowKeys)
        })
        const payload = {
            playbookQuestionId: data?.id,
            year: data?.year || defaultYear,
            filterType: event,
            sourceType: event !== 'All' ? data?.sourceType : [],
            sourceSubType: event !== 'All' ? data?.sourceSubType : [],
            finalUnits: data?.finalUnits,
            dataType: data?.dataType,
        }
        dispatch(questionWiseUpdate(payload))
    }

    useEffect(() => {
        if (data?.filterType) {
            setFilterType((prev) => ({
                ...prev,
                [data.id]: data?.filterType,
            }));
        }
    }, [data?.filterType, data?.id]);

    return (
        <div>
            <Select
                className="source-select"
                options={filterOptions}
                placeholder="Select Filter"
                value={filterType[data.id] || data?.filterType}
                onChange={(e) => updateQuestionFilterType(e, data)}
                filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
            />
        </div>
    )
}

const SourceType = ({ setFilterType, data, dispatch, sourceTypeOptions, updatedQuestionRow, setUpdatedQuestionRow, setSelectedRowKeys, defaultYear }) => {
    const [sourceOptionSearchValue, setSourceOptionSearchValue] = useState('')
    const [selectedValue, setSelectedValue] = useState([...data?.sourceSubType, ...data?.sourceType])

    const onSearchSource = (e) => {
        setSourceOptionSearchValue(e)
    }

    const updateQuestionSourceType = (values, question) => {
        if (values?.length > 0) {
            const sourceParentChildDetails = getParentAndChildrenOptions({
                data: sourceTypeOptions,
                selectedSource: values,
            })
            const payload = {
                playbookQuestionId: data?.id,
                year: data?.year || defaultYear,
                filterType: data?.filterType,
                sourceType: sourceParentChildDetails?.parents,
                sourceSubType: sourceParentChildDetails?.children,
                finalUnits: data?.finalUnits,
                dataType: data?.dataType,
            }

            dispatch(questionWiseUpdate(payload))

            setSelectedValue([...sourceParentChildDetails?.parents, ...sourceParentChildDetails?.children])

            setSelectedRowKeys((prevData) => {
                const newRowKeys = new Set(prevData)
                newRowKeys.add(question?.id)
                return Array.from(newRowKeys)
            })

            updateQuestionRow(question, "sourceType", sourceParentChildDetails?.parents, setUpdatedQuestionRow);
            updateQuestionRow(question, "sourceSubType", sourceParentChildDetails?.children, setUpdatedQuestionRow);
        } else {
            const payload = {
                playbookQuestionId: data?.id,
                year: data?.year || defaultYear,
                filterType: 'All',
                sourceType: [],
                sourceSubType: [],
                finalUnits: data?.finalUnits,
                dataType: data?.dataType,
            }
            dispatch(questionWiseUpdate(payload))
            setUpdatedQuestionRow((prev) =>
                prev.map((item) =>
                    item.id === question.id
                        ? { ...item, sourceType: [], sourceSubType: [], filterType: "All" }
                        : item
                )
            );

            setFilterType((prev) => ({
                ...prev,
                [question.id]: "All",
            }));
            setSelectedValue([])
        }
    }
    const disableSourceType = updatedQuestionRow?.find((item) => item.id === data?.id)?.filterType || data?.filterType;

    useEffect(() => {
        if (data?.filterType === 'All') {
            setSelectedValue([])
        }
    }, [data?.filterType])

    return (
        <div>
            <TreeSelect
                className="source-select"
                popupClassName="filters-select"
                disabled={disableSourceType === 'All'}
                multiple
                allowClear
                showSearch
                treeCheckable
                maxTagCount={1}
                value={selectedValue}
                treeData={sourceTypeOptions}
                searchValue={sourceOptionSearchValue}
                showCheckedStrategy="SHOW_PARENT"
                placeholder="Select Disclosure Type"
                onSearch={onSearchSource}
                onChange={(e) => updateQuestionSourceType(e, data)}
                filterTreeNode={(input, option) => {
                    return option?.key?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0 || option?.title?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0
                }}
            />
        </div>
    )
}

const PrefillSourceYear = ({ data, selectedSourceYear, setUpdatedQuestionRow, setSelectedSourceYear, allowChange, yearOptions }) => {

    const handleSourceYearChange = (value) => {
        if (allowChange) {
            setSelectedSourceYear(value)
        }
        setUpdatedQuestionRow((prev) =>
            data.map(item => ({
                ...item,
                year: value,
                isUpdated: true
            }))
        );
    }

    return (
        <div className="custom-table-header-action-contents">
            <div className="wrapper-text">Prefill Source Year</div>
            <div className="input-container">
                <Select
                    showSearch
                    allowClear
                    className="filter-select"
                    options={yearOptions}
                    onChange={handleSourceYearChange}
                    value={selectedSourceYear}
                    placeholder="Select Source Year"
                    filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                />
            </div>
            {!allowChange && <div className="warning-text">No questions available!</div>}
        </div>
    )
}

const CreateBatch = ({ setHideSidebar }) => {

    const user_decrypt = decryptData(sessionStorage.getItem('rqewseu'))
    let role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'))
    const isPromptAdmin = role_decrypt?.role === "prompt_admin"
    const isAdmin = role_decrypt?.role === "admin"

    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [batchName, setBatchName] = useState('')
    const [language, setLanguage] = useState(null)
    const [selectedModel, setSelectedModel] = useState("haiku");
    const [yearOptions, setYearOptions] = useState([])
    const [showFilters, setShowFilters] = useState(true)
    const [selectedPlaybook, setSelectedPlaybook] = useState(null)
    const [selectedPlaybookId, setSelectedPlaybookId] = useState(null)
    const [selectedEntity, setSelectedEntity] = useState([])
    const [selectedEntityId, setSelectedEntityId] = useState(null)
    const [sourceTypeOptions, setSourceTypeOptions] = useState([])
    const [selectedRowKeys, setSelectedRowKeys] = useState([])
    const [selectedYear, setSelectedYear] = useState(null)
    const [showReviewModal, setShowReviewModal] = useState(false)
    const [selectedFormat, setSelectedFormat] = useState(null)
    const [selectedTaxonomy, setSelectedTaxonomy] = useState(null)
    const [formattedTableData, setFormattedTableData] = useState({ dataSource: [], columns: [] })
    const [playBookResponse, setPlayBookResponse] = useState(null)
    const [updatedQuestionRow, setUpdatedQuestionRow] = useState([])
    const [showPrioritize, setShowPrioritize] = useState(false)
    const [selectedQuestion, setSelectedQuestion] = useState(null)
    const [searchValue, setSearchValue] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [isBulkPriorityDisable, setIsBulkPriorityDisable] = useState(true);
    const [selectedSourceYear, setSelectedSourceYear] = useState(null)
    const [filterType, setFilterType] = useState({})
    const [selectedBatchMode, setSelectedBatchMode] = useState("Public")
    const [selectedUser, setSelectedUser] = useState(null)
    const [selectedFolder, setSelectedFolder] = useState([])
    const [folderList, setFolderList] = useState([])
    const [selectedProject, setSelectedProject] = useState(null);

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { data: createPlaybookBatchdata, success: createPlaybookBatchSuccess, isLoading: createPlaybookBatchLoading, error: createPlaybookBatchError } = useSelector((state) => state.createPlaybookBatch)
    const { data: viewPlaybookResponse, isLoading: viewPlaybookLoading } = useSelector((state) => state?.playbookBatchQuestions)
    const { data: disclosureTypesData } = useSelector((state) => state?.disclosureTypes)
    const { success: questionUpdatedSuccess } = useSelector((state) => state?.questionWiseUpdate)
    const { data: dropdownData, isLoading: dropdownDataLoading } = useSelector((state) => state.playbookNameDropdown)
    const { isLoading: prefillSourceYearLoading } = useSelector((state) => state.prefillPlaybookSourceYear)
    const { data: userWiseFolderList } = useSelector((state) => state.userWiseFolderList)

    const parentSource = sourceTypeOptions?.map((item) => item.value)
    const childSource = sourceTypeOptions?.flatMap((item) => item.children.map((childItem) => childItem.value))

    const batchModeOptions = [
        { label: "Public", value: "Public" },
        { label: "Private", value: "Private" }
    ]

    const isPrivateBatch = selectedBatchMode === "Private";
    const isPublicBatch = selectedBatchMode === "Public";

    const handleShowFiltersSection = () => {
        setShowFilters(!showFilters)
    }

    //  const handleFolderSelectChange = (selectedValues, _, currentItem) => {

    //     const maxFoldersToSelect = 1;

    //     if ((selectedValues.length > maxFoldersToSelect) && currentItem?.checked) {
    //       message.error(`Maximum limit to select is ${maxFoldersToSelect}`);
    //     } else {
    //       const newSelection = selectedValues.find(
    //         (item) => !selectedFolder.some((folder) => folder.value === item.value)
    //       );

    //       if (newSelection) {
    //         const selectedNode = findNodeByValue(
    //           newSelection.value,
    //           folderList
    //         );
    //         let nodesToAdd = [
    //           { label: selectedNode.title, value: selectedNode.value },
    //         ];

    //         if (selectedNode.children?.length) {
    //           nodesToAdd = [...nodesToAdd, ...getAllDescendantNodes(selectedNode)];
    //         }

    //         const allSelectedNodes = [...selectedFolder, ...nodesToAdd];
    //         const uniqueSelectedNodes = Array.from(
    //           new Map(allSelectedNodes.map((item) => [item.value, item])).values()
    //         );

    //         setSelectedFolder(uniqueSelectedNodes);
    //       } else {
    //         const missingFolders = selectedFolder.filter(
    //           (folder) =>
    //             !selectedValues.some((selected) => selected.value === folder.value)
    //         );

    //         if (missingFolders.length) {
    //           const missingFolderValue = missingFolders[0].value;

    //           const updatedSelectedValues = selectedValues.filter(
    //             (selected) => !selected.value.includes(missingFolderValue)
    //           );

    //           setSelectedFolder(updatedSelectedValues);
    //         } else {
    //           setSelectedFolder(selectedValues);
    //         }
    //       }
    //     }
    //   };

    const handleFolderSelectChange = (values) => {
        const maxFoldersToSelect = 5;

        if (values.length > maxFoldersToSelect) {
            message.error(`Maximum limit to select is ${maxFoldersToSelect}`);
        } else {
            setSelectedFolder(values);
        }
    }

    const onSelectChange = (newSelectedRowKeys, selectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(selectedRows);
        setSelectedQuestion(null);

        let updatedRows = [...updatedQuestionRow];

        newSelectedRowKeys.forEach((key) => {
            let existingRowIndex = updatedRows.findIndex(row => row.id === key);

            if (existingRowIndex === -1) {
                let newRow = viewPlaybookResponse.fetchPlaybookQuestions.find(row => row.id === key);
                if (newRow) {
                    updatedRows.push({ ...newRow, isUpdated: true });
                }
            } else {
                updatedRows[existingRowIndex].isUpdated = true;
            }
        });
        updatedRows = updatedRows.map(row => ({
            ...row,
            isUpdated: newSelectedRowKeys.includes(row.id) ? row.isUpdated : false
        }));
        updatedRows = updatedRows.filter((row, index, self) =>
            index === self.findIndex((r) => r.id === row.id)
        );

        setUpdatedQuestionRow(updatedRows);

        if (newSelectedRowKeys.length > 1) {
            setIsBulkPriorityDisable(false);
        } else {
            setIsBulkPriorityDisable(true);
        }
    };

    const handleSearch = (value) => {
        setSearchValue(value);
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            searchBasedFilter();
        }
    }

    const searchBasedFilter = () => {
        const searchTerm = searchValue?.trim().toLowerCase();

        const filteredData = playBookResponse?.filter(({ dpCode, criteria }) => {
            const dpCodeText = dpCode?.toLowerCase() || '';
            const criteriaText = criteria?.replace(/[\r\n]+/g, '')?.toLowerCase() || '';
            return dpCodeText?.includes(searchTerm) || criteriaText?.includes(searchTerm);
        });
        setFormattedTableData({ ...formattedTableData, dataSource: getStructuredData(filteredData) });
    };

    const handleBulkPriority = () => {
        setShowPrioritize(true);
        setSelectedQuestion(null);
    }

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    }

    const firstPlaybookPayload = {
        isDropDownValues: true,
        isCustomBatch: isPrivateBatch ? true : false
    }

    const handleBatchName = (e) => {
        setBatchName(e.target.value)
    }

    const handlePlaybookChange = (value, label) => {
        setSelectedPlaybook(label)
        setSelectedPlaybookId(value)
        setSelectedSourceYear(null)
        setSelectedRowKeys([])

        if (!value || !label) {
            setSelectedRowKeys([])
        }
    }

    const handleBatchModeChange = (mode) => {
        setSelectedBatchMode(mode)
    }

    const handleProjectChange = (value) => {
        setSelectedProject(value)
    }

    const handleUserChange = (user) => {
        setSelectedUser(user)
        setSelectedFolder([])
    }

    const handleFormatChange = (value, label) => {
        setSelectedFormat(label)

        if (!value || !label) {
            setSelectedRowKeys([])
        }
    }

    const handleTaxonomyChange = (e) => {
        setSelectedTaxonomy(e)
    }

    const handleYearChange = (value) => {
        setSelectedYear(value)
    }

    const handleEntityChange = (value, label) => {
        if (value?.length <= 50) {
            setSelectedEntity(label)
            setSelectedEntityId(value)
        } else {
            message.error('Maximum limit reached')
        }
    }

    const handleLanguageChange = (value) => {
        setLanguage(value)
    }

    const handleModelChange = (value) => {
        setSelectedModel(value);
    }

    const getTableHeaderData = () => {
        let baseHeaders = [
            {
                title: 'DP Code',
                dataIndex: 'dpCode',
                key: 'dpCode',
                width: '10%',
                sorter: (a, b) => a.dpCode.localeCompare(b.dpCode),
            },
            {
                title: 'Parent',
                dataIndex: 'parent',
                key: 'parent',
                width: '10%',
                sorter: (a, b) => a.parent.localeCompare(b.parent),
            },
            {
                title: 'Questions',
                dataIndex: 'criteria',
                key: 'criteria',
                width: isPrivateBatch ? '90%' : selectedFormat?.value === 'json' ? '60%' : '40%',
            }
        ]
        if (isPrivateBatch) {
            return baseHeaders;
        }
        let table_headers = [
            ...baseHeaders,
            {
                title: (
                    <div className="custom-table-column-header-wrapper">
                        <div className="header-title">Source Year</div>
                        <Dropdown
                            dropdownRender={() => (
                                <PrefillSourceYear setUpdatedQuestionRow={setUpdatedQuestionRow} selectedSourceYear={selectedSourceYear} setSelectedSourceYear={setSelectedSourceYear} data={viewPlaybookResponse?.fetchPlaybookQuestions} dispatch={dispatch} playbookId={selectedPlaybookId} allowChange={formattedTableData?.columns?.length > 0} yearOptions={yearOptions} />
                            )}
                        >
                            <Tooltip title="Prefill Source Year">
                                <div className="action-item">
                                    <FaEdit />
                                </div>
                            </Tooltip>
                        </Dropdown>
                    </div>
                ),
                dataIndex: 'year',
                key: 'year',
                width: '20%',
            },
        ]
        if (selectedFormat?.value === 'json') {
            table_headers.push({
                title: 'Prioritize Source',
                dataIndex: 'sourcePriority',
                key: 'sourcePriority',
                width: '10%',
                align: 'center',
            });

        } else {
            table_headers.push({
                title: 'Filter',
                dataIndex: 'filter',
                key: 'filter',
                width: '10%',
            },
                {
                    title: 'Disclosure Type',
                    dataIndex: 'sourcetype',
                    key: 'sourcetype',
                    width: '20%',
                },
            )
        }

        return table_headers
    }

    const getStructuredData = (arr) => {
        const structuredDataSource = arr?.map((data, index) => {
            return {
                key: data?.id ? data?.id : index,
                dpCode: data?.dpCode ? data?.dpCode : '',
                parent: data?.parentDpCode ? data?.parentDpCode : '',
                criteria: data?.criteria ? data?.criteria : '',
                year: (
                    <Year data={data} dispatch={dispatch} viewPlaybookResponse={viewPlaybookResponse} yearOptions={yearOptions} setUpdatedQuestionRow={setUpdatedQuestionRow} defaultYear={selectedSourceYear} setSelectedRowKeys={setSelectedRowKeys} />
                ),
                filter: (
                    <FilterType
                        index={index}
                        data={data}
                        updatedQuestionRow={updatedQuestionRow}
                        dispatch={dispatch}
                        setUpdatedQuestionRow={setUpdatedQuestionRow}
                        defaultYear={selectedSourceYear}
                        parentSource={parentSource}
                        childSource={childSource}
                        setSelectedRowKeys={setSelectedRowKeys}
                        setFilterType={setFilterType}
                        filterType={filterType}
                    />
                ),
                sourcetype: (
                    <SourceType
                        data={data}
                        setFilterType={setFilterType}
                        dispatch={dispatch}
                        updatedQuestionRow={updatedQuestionRow}
                        sourceTypeOptions={sourceTypeOptions}
                        defaultYear={selectedSourceYear}
                        setUpdatedQuestionRow={setUpdatedQuestionRow}
                        setSelectedRowKeys={setSelectedRowKeys}
                    />
                ),
                sourcePriority: (
                    <div className="action-buttons-container">
                        <button
                            className={`action-button`}
                            onClick={() => {
                                setShowPrioritize(!showPrioritize)
                                setSelectedQuestion(data)
                                setSelectedRows([]);
                            }}
                            disabled={!sourceTypeOptions.length}
                        >
                            {data?.sourcePriority?.isEdited ? 'Edit' : 'Set'}
                        </button>
                    </div>
                ),
            }
        })

        return structuredDataSource
    }

    const updatingTableValues = (updatedRow) => {
        const updatedRecords = []
        for (let rowIndex = 0; rowIndex < playBookResponse?.length; rowIndex++) {
            if (playBookResponse[rowIndex]?.id === updatedRow?.id) {
                updatedRecords.push(updatedRow)
            } else {
                updatedRecords.push(playBookResponse[rowIndex])
            }
        }
        setPlayBookResponse(updatedRecords)
    }

    const handleConfirmCreate = () => {
        if (createPlaybookBatchLoading) return;
        const playbookQuestionIds = updatedQuestionRow
            ?.filter(item => selectedRowKeys.includes(item.id) && item.isUpdated === true)
            .map(item => ({ id: item.id, year: isPrivateBatch ? "" : item.year }));

        const payload = {
            playbookbatch: [
                {
                    projectMasterId: selectedProject,
                    playbookBatchName: batchName,
                    playbookId: selectedPlaybookId,
                    year: isPublicBatch ? [selectedYear] : [],
                    playbookQuestionIds,
                    documentLanguage: language,
                    reportFormat: isPrivateBatch ? "json" : selectedFormat?.value,
                    entityIds: isPublicBatch ? selectedEntityId : [],
                    answeringModel: selectedModel,
                    taxonomyId: selectedTaxonomy ? selectedTaxonomy : "others",
                    isCustomBatch: isPrivateBatch ? true : false,
                    folderPath: (isPrivateBatch && selectedFolder) ? selectedFolder : []
                },
            ],
        }

        dispatch(createPlaybookBatch(payload))
    }

    const handleCreateBatch = () => {
        try {
            const requiredFields = {
                Private: [batchName, selectedPlaybook, selectedPlaybookId, selectedUser, selectedModel, selectedFolder],
                Public: [batchName, selectedPlaybook, selectedPlaybookId, selectedFormat, selectedModel, selectedYear, selectedEntityId, selectedTaxonomy, selectedProject]
            };

            const missingFields = requiredFields[selectedBatchMode]?.some(field => !field || (Array.isArray(field) && field.length === 0));

            if (missingFields) {
                message.error('Please fill all required fields before proceeding.');
                return;
            }
            if (selectedRowKeys.length === 0) {
                message.error('Please select the questions before proceeding.')
                return
            }
            if (selectedRowKeys.length > 0) {
                const invalidRow = isPublicBatch ? updatedQuestionRow.filter((item) => {
                    if (!selectedRowKeys.includes(item.id)) return false;
                    if (!item.isUpdated) return false;

                    if (selectedFormat === "json") {
                        return !item.year || item.year === "";
                    }

                    if (!item.year || item.year === "") {
                        return true;
                    }

                    if (item.filterType !== "All") {
                        return (!item.sourceSubType || item.sourceSubType.length === 0) &&
                            (!item.sourceType || item.sourceType.length === 0);
                    }

                    return false;
                }) : "";

                if (invalidRow?.length > 0) {
                    message.error("Please fill out the missing fields on selected rows")
                    return
                }
                const selectedDpCodes = new Set(selectedRows?.map(item => item?.dpCode));
                const invalidDpCodes = selectedRows
                    .filter(item => item.parent && !selectedDpCodes.has(item.parent))
                    .map(item => item.dpCode);
                if (invalidDpCodes?.length > 0) {
                    message.error(
                        <span>
                            Please select the parentDP for <strong>{invalidDpCodes.join(", ")}</strong>
                        </span>
                    )
                    return;
                }
            }
            setShowReviewModal(true)
            return

        } catch (error) {
            message.error('An unexpected error occurred. Please try again.')
        }
    }

    const onCloseSourcePriority = (getQuestions) => {

        setShowPrioritize(!showPrioritize)
        setSelectedQuestion(null)
        if (getQuestions)
            dispatch(playbookBatchQuestions({
                playbookId: selectedPlaybookId,
            }))
    }

    useEffect(() => {
        setHideSidebar(true)
    }, [])

    useEffect(() => {
        const getYears = (startYear = 2018) => {
            const currentYear = new Date().getFullYear() + 1
            return Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
                const year = startYear + i
                return { value: `FY-${year}`, label: `FY-${year}` }
            })
        }
        setYearOptions(getYears())
    }, [])

    useEffect(() => {
        dispatch(playbookNameDropdown(firstPlaybookPayload))
        setFormattedTableData({ columns: getTableHeaderData(), dataSource: [] })
        setBatchName("");
        setSelectedPlaybook(null);
        setSelectedFormat(null);
        setLanguage(null);
        setSelectedTaxonomy(null);
        setSelectedFolder([])
        setSelectedUser(null)
        setSelectedPlaybookId(null)
        setSelectedProject(null)
    }, [selectedBatchMode])

    useEffect(() => {
        if (selectedPlaybookId && (isPrivateBatch || selectedFormat)) {
            dispatch(playbookBatchQuestionsReset())
            const questionsPayload = {
                playbookId: selectedPlaybookId,
            }
            dispatch(playbookBatchQuestions(questionsPayload))
        } else if (!selectedPlaybookId) {
            setPlayBookResponse([])
            dispatch(playbookBatchQuestionsReset())
        }

    }, [selectedPlaybookId, selectedFormat])

    useEffect(() => {
        if (dropdownData?.customFolderUserList && user_decrypt?.userName) {
            const matchedUser = dropdownData.customFolderUserList.find(
                (user) => user?.label === user_decrypt.userName
            );
            if (matchedUser) {
                setSelectedUser(matchedUser.value);
            }
        }
    }, [dropdownData]);

    useEffect(() => {
        if (userWiseFolderList?.data) {
            const filteredFolders = filterFolders(userWiseFolderList.data);

            if (userWiseFolderList?.data?.length > 0) {
                setFolderList([...filteredFolders]);
            } else {
                setFolderList(filteredFolders);
            }
        }
    }, [userWiseFolderList?.data]);

    useEffect(() => {
        if (selectedUser && isPrivateBatch) {
            const payload = {
                uploadedBy: selectedUser
            }
            dispatch(getUserFolderDetails(payload))
        }
    }, [selectedUser, selectedBatchMode])

    // useEffect(() => {
    //     if(isPromptAdmin){
    //         setSelectedModel("haiku")
    //     }
    // },[isPromptAdmin])

    useEffect(() => {
        if (selectedSourceYear && selectedPlaybookId) {
            dispatch(playbookBatchQuestionsReset())
            dispatch(PrefillPlaybookSourceYearReset())

            const questionsPayload = {
                playbookId: selectedPlaybookId,
            }

            dispatch(playbookBatchQuestions(questionsPayload))

        }
    }, [selectedSourceYear])

    useEffect(() => {
        if (playBookResponse) {
            setFormattedTableData({
                columns: getTableHeaderData(),
                dataSource: getStructuredData(playBookResponse),
            })
        }
    }, [playBookResponse, sourceTypeOptions, selectedBatchMode])

    useEffect(() => {
        if (viewPlaybookResponse && viewPlaybookResponse?.fetchPlaybookQuestions) {
            setPlayBookResponse(viewPlaybookResponse?.fetchPlaybookQuestions)
        } else {
            setPlayBookResponse([])
        }
    }, [viewPlaybookResponse?.fetchPlaybookQuestions])

    useEffect(() => {
        if (disclosureTypesData && disclosureTypesData?.disclosureTypes) {
            setSourceTypeOptions(disclosureTypesData?.disclosureTypes)
        } else {
            setSourceTypeOptions([])
        }
    }, [selectedPlaybook, disclosureTypesData])

    useEffect(() => {
        if (questionUpdatedSuccess) {
            updatingTableValues(updatedQuestionRow);
        }
    }, [questionUpdatedSuccess, updatedQuestionRow]);

    useEffect(() => {
        if (selectedPlaybookId) {
            dispatch(disclosureTypes())
        }
    }, [selectedPlaybookId])

    useEffect(() => {
        if (createPlaybookBatchSuccess) {
            message.success(createPlaybookBatchdata?.message)
            navigate(uris?.playbookBatch)
        }
        if (createPlaybookBatchError?.message) {
            message.error(createPlaybookBatchError?.message)
        }
        dispatch(createPlaybookBatchReset())
    }, [createPlaybookBatchError, createPlaybookBatchSuccess])

    return (
        <div className="create-batch-main">
            <Breadcrumbs breadcrumbDetails={menuData} />
            <div className="run-batch-filters">
                {showFilters && (
                    <div className="show-filters-section">
                        <div className="run-batch-filters-section">

                            {/* Batch Mode */}
                            <div className="input-wrapper">
                                <div className="input-label">Batch Mode <span>*</span></div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        className="filter-select"
                                        options={batchModeOptions || []}
                                        onChange={handleBatchModeChange}
                                        value={selectedBatchMode}
                                        placeholder="Select Mode"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>

                            {/* Batch Name */}
                            <div className="input-wrapper">
                                <div className="input-label">
                                    Batch Name <span>*</span>
                                </div>
                                <div className="input-container">
                                    <Input className="filter-select" onChange={(e) => handleBatchName(e)} value={batchName} placeholder="Enter Batch Name" />
                                </div>
                            </div>

                            {/* Playbook Name */}
                            <div className="input-wrapper">
                                <div className="input-label">
                                    Playbook Name <span>*</span>
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        options={dropdownData?.filterData || []}
                                        onChange={handlePlaybookChange}
                                        value={selectedPlaybook}
                                        placeholder="Select Playbook"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                        notFoundContent={dropdownDataLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : "No Data"}
                                    />
                                </div>
                            </div>

                            {/* Format */}
                            {isPublicBatch && <div className="input-wrapper">
                                <div className="input-label">
                                    Format <span>*</span>
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        options={dropdownData?.reportFormats || []}
                                        onChange={handleFormatChange}
                                        value={selectedFormat}
                                        placeholder="Select Format"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>}

                            {/* Task Year */}
                            {isPublicBatch && <div className="input-wrapper">
                                <div className="input-label">
                                    Task Year <span>*</span>
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        options={yearOptions}
                                        onChange={handleYearChange}
                                        value={selectedYear}
                                        placeholder="Select Task Year"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>}

                            {/* User */}
                            {isPrivateBatch && <div className="input-wrapper">
                                <div className="input-label">
                                    User <span>*</span>
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        className="filter-select"
                                        options={dropdownData?.customFolderUserList || []}
                                        onChange={handleUserChange}
                                        value={selectedUser}
                                        placeholder="Select User"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>}

                            {/* Custom Folder */}
                            {isPrivateBatch && <div className="input-wrapper">
                                <div className="input-label">
                                    Folder <span>*</span>
                                </div>
                                <div className="input-container">
                                    <TreeSelect
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        treeCheckable
                                        maxTagCount={1}
                                        showCheckedStrategy="SHOW_PARENT"
                                        treeData={folderList || []}
                                        value={selectedFolder}
                                        onChange={handleFolderSelectChange}
                                        placeholder="Select Folder"
                                        filterTreeNode={(input, option) => {
                                            return (
                                                option?.key
                                                    ?.toLowerCase()
                                                    ?.indexOf(input?.toLowerCase()) >= 0 ||
                                                option?.title
                                                    ?.toLowerCase()
                                                    ?.indexOf(input?.toLowerCase()) >= 0
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                            }

                            {/* Entity */}
                            {isPublicBatch && <div className="input-wrapper">
                                <div className="input-label">
                                    Entity<span>*</span>
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        mode="multiple"
                                        allowClear
                                        className="filter-select"
                                        options={dropdownData?.entitiesFilterData || []}
                                        onChange={handleEntityChange}
                                        value={selectedEntity}
                                        placeholder="Select Entity"
                                        optionFilterProp="label"
                                        maxTagCount={1}
                                        maxTagTextLength={4}
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>}

                            {/* Project Selection */}
                            {isPublicBatch && <div className="input-wrapper">
                                <div className="input-label">
                                    Project <span>*</span>
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        options={dropdownData?.projectMasterData || []}
                                        onChange={handleProjectChange}
                                        value={selectedProject}
                                        placeholder="Select Project"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>}

                            {/* Taxonomy */}
                            {isPublicBatch && <div className="input-wrapper">
                                <div className="input-label">
                                    Taxonomy <span>*</span>
                                </div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        options={dropdownData?.taxonomyList || []}
                                        onChange={handleTaxonomyChange}
                                        value={selectedTaxonomy}
                                        placeholder="Select Taxonomy"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>}

                            {/* Model */}
                           <div className="input-wrapper">
                                <div className="input-label">Model<span>*</span></div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        options={ModelOptions || []}
                                        onChange={(value) => handleModelChange(value)}
                                        value={selectedModel}
                                        placeholder="Select Model"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>

                            {/* Language */}
                            {isPublicBatch && <div className="input-wrapper">
                                <div className="input-label">Language</div>
                                <div className="input-container">
                                    <Select
                                        showSearch
                                        allowClear
                                        className="filter-select"
                                        options={dropdownData?.languageList || []}
                                        onChange={(value) => handleLanguageChange(value)}
                                        value={language}
                                        placeholder="Select Language"
                                        filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    />
                                </div>
                            </div>}
                            {/* <ManageFilters showFilters={showFilters} handleShowFiltersSection={handleShowFiltersSection} customStyle={{ display: 'flex' }} /> */}
                        </div>
                    </div>
                )}
                <ManageFilters showFilters={showFilters} handleShowFiltersSection={handleShowFiltersSection} customStyle={{ display: 'flex' }} />
                <div className='search-filters-section'>
                    <div className='search-wrapper'>
                        <input type="text" className="search-input" placeholder="Search By Dpcode or Question" value={searchValue} onChange={(e) => handleSearch(e?.target?.value)} onKeyDown={(e) => handleKeyPress(e)} />
                        <FaSearch className={`search-icon ${searchValue ? 'active-icon' : ''}`} onClick={() => searchBasedFilter()} />
                    </div>
                    <div className='bulk-priority-button-section'>
                        <div className="button-wrapper create-batch-btn" onClick={() => handleCreateBatch()}>Create</div>
                        {
                            (selectedFormat?.value === "json" && isPublicBatch) &&
                            <div className={`button-wrapper ${isBulkPriorityDisable ? 'disabled-bulk-button' : 'enabled-bulk-button'}`} onClick={() => handleBulkPriority()}>Set Priority</div>
                        }
                    </div>
                </div>
            </div>

            <div className="create-batch-table-section">
                <CustomTable
                    page={page}
                    limit={limit}
                    rowSelection={rowSelection}
                    setPage={setPage}
                    setLimit={setLimit}
                    tableData={formattedTableData}
                    totalRows={formattedTableData?.dataSource?.length}
                    isLoading={viewPlaybookLoading || prefillSourceYearLoading}
                />
            </div>
            {showPrioritize && (
                <PrioritizeSource
                    open={showPrioritize}
                    onClose={(e) => onCloseSourcePriority(e)}
                    sourceOptions={sourceTypeOptions}
                    selectedQuestion={selectedQuestion}
                    selectedRows={selectedRows}
                    selectedRowKeys={selectedRowKeys}
                />
            )}

            {showReviewModal && (
                <CustomModal
                    modalTitle={
                        <div className="modal-title">
                            <div className="modal-icon">
                                <PiWarningCircleLight />
                            </div>
                            Confirm
                        </div>
                    }
                    handleClose={() => setShowReviewModal(false)}
                    showCloseIcon
                >
                    <div className="confirmation-body">
                        <p className="modal">
                            Are you sure you want to create a batch ?
                        </p>
                    </div>
                    <div className="action-buttons">
                        <div className={`button-wrapper yes ${createPlaybookBatchLoading ? "disabled" : ""}`} onClick={handleConfirmCreate}>
                            {createPlaybookBatchLoading ? <Spin indicator={antIcon} /> : "Yes"}
                        </div>
                        <div className="button-wrapper no" onClick={() => setShowReviewModal(false)}>
                            No
                        </div>
                    </div>
                </CustomModal>
            )}
        </div>
    )
}

export default CreateBatch
